import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import './appSidebar.css';

const isChildActive = (pathname, child) => {
    if (!child.path) return false;
    if (child.exact) return pathname === child.path;
    return pathname === child.path || pathname.startsWith(`${child.path}/`);
};

const AppSidebar = ({ groups, sections }) => {
    const location = useLocation();

    const visibleGroups = useMemo(
        () =>
            groups
                .filter((group) => group.display !== false)
                .map((group) => ({
                    ...group,
                    children: group.children.filter((child) => child.display !== false),
                }))
                .filter((group) => group.children.length > 0),
        [groups]
    );

    const activeGroupKey = useMemo(() => {
        const match = visibleGroups.find((group) =>
            group.children.some((child) => isChildActive(location.pathname, child))
        );
        return match ? match.key : null;
    }, [visibleGroups, location.pathname]);

    const [openGroups, setOpenGroups] = useState(() =>
        activeGroupKey ? { [activeGroupKey]: true } : {}
    );

    useEffect(() => {
        if (activeGroupKey) {
            setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
        }
    }, [activeGroupKey]);

    const toggleGroup = (key) => {
        setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <nav className="idb-sidebar" aria-label="Section navigation">
            {sections && sections.length > 0 && (
                <div className="idb-section-switch" role="tablist">
                    {sections.map((section) => (
                        <NavLink
                            key={section.key}
                            to={section.path}
                            exact={!!section.exact}
                            isActive={() => isChildActive(location.pathname, section)}
                            className="idb-section-btn"
                            activeClassName="is-active"
                        >
                            {section.label}
                        </NavLink>
                    ))}
                </div>
            )}
            {visibleGroups.map((group) => {
                const isOpen = !!openGroups[group.key];
                const isActiveGroup = group.key === activeGroupKey;
                return (
                    <div className="idb-group" key={group.key}>
                        <button
                            type="button"
                            className={`idb-group-header${isActiveGroup ? ' is-active' : ''}`}
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={isOpen}
                        >
                            <span className="idb-group-icon">{group.icon}</span>
                            <span className="idb-group-label">{group.label}</span>
                            <svg
                                className={`idb-chevron${isOpen ? ' is-open' : ''}`}
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden="true"
                            >
                                <path
                                    d="M6 9l6 6 6-6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {isOpen && (
                            <ul className="idb-group-children">
                                {group.children.map((child) => (
                                    <li key={child.path}>
                                        <NavLink
                                            to={child.path}
                                            exact={!!child.exact}
                                            isActive={() => isChildActive(location.pathname, child)}
                                            className="idb-child-link"
                                            activeClassName="is-active"
                                        >
                                            <span className={child.disabled ? 'idb-locked' : ''}>
                                                {child.label}
                                            </span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
        </nav>
    );
};

AppSidebar.propTypes = {
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
            exact: PropTypes.bool,
        })
    ),
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.node,
            children: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    path: PropTypes.string.isRequired,
                    exact: PropTypes.bool,
                    display: PropTypes.bool,
                    disabled: PropTypes.bool,
                })
            ).isRequired,
        })
    ).isRequired,
};

export default AppSidebar;
